import { AppStateContainer } from './AppStateContainer';
import DebugService from '../debug.service';

// =====
//
// NOTE: See AppStateProvider.spec.js for a full walkthrough of the AppStateProvider system!
//
// Based on https://github.com/FormidableLabs/freactal
//
// =====

const log = (...args) => DebugService.log('AppStateProvider', ...args);
const logError = (...args) => DebugService.error('AppStateProvider', ...args);

export namespace AppStateProv {
    export interface State {
        [key: string]: any;
    }

    export type StateUpdateFn = (state: State) => State;

    interface KeyDefinition {
        initialValue?;
        defaultValue?;
        eqCheck?;
        validator?;
        tags?;
    }

    interface KeyDefinitions {
        [key: string]: KeyDefinition;
    }

    export interface StateUpdateConfig {
        updateFn: StateUpdateFn;
        silenceListenersToTag: string;
    }

    export type EffectDefinition = (...effectArgs) => StateUpdateFn | StateUpdateConfig;
    export type AsyncEffectDefinition = (
        ...effectArgs
    ) => Promise<StateUpdateFn | StateUpdateConfig>;

    export interface EffectDefinitions {
        [key: string]: EffectDefinition | AsyncEffectDefinition;
    }

    export interface SubscriptionOptions {
        // should probably be sets? but makes for an annoying api
        keys?: Array<string>;
        tags?: Array<string>;
        runNow?: boolean;
    }

    export type SubscriptionCallback = (
        newState: State,
        oldState: State,
        changedKeys: Set<string>,
    ) => void;

    export interface ProviderOptions {
        keys?: KeyDefinitions;
        effects?: EffectDefinitions;
        parent?: AppStateProvider;
        effectsCanMutateKeys?: boolean;
    }
}

export function mergeIntoState(obj: object): AppStateProv.StateUpdateFn {
    return (state) => Object.assign({}, state, obj);
}

// TODO split this helper into two; one that takes a function, another that takes an object
//
// simpleEffect() is a helper function for making effect definitions
//
// When simpleEffect() is passed an object it returns an EffectDefinition that simply merges the object into state.
//  - useful when you don't want to care about the current state, just set the value of some keys
//  - any arguments passed to the effect when it is called later will be ignored
//
// When simpleEffect() is passed a function it returns an EffectDefinition that
// merges into state the result of calling that function with:
//  - current state as the first param
//  - the arguments provided to the effect when it is called later 'spread' as the rest of the params
export function simpleEffect(
    arg: ((state: AppStateProv.State, ...effectArgs) => AppStateProv.State) | AppStateProv.State,
): AppStateProv.EffectDefinition {
    if (typeof arg === 'function') {
        return (...args) =>
            (state) =>
                Object.assign({}, state, arg(state, ...args));
    } else if (typeof arg === 'object' && arg !== null) {
        return () => (state) => Object.assign({}, state, arg);
    } else {
        throw new Error('First param to simpleEffect() must be a function or non-null object');
    }
}

export class AppStateProvider {
    private _parent?: AppStateProvider;
    private _stateContainer?: AppStateContainer;
    private _tags?: { [tag: string]: Array<string> };
    private _subscribers?: any;
    private _nextSubscriberId?: number;
    effects: any;

    constructor({
        keys,
        effects,
        parent,
        effectsCanMutateKeys,
    }: AppStateProv.ProviderOptions = {}) {
        const keyDetails = {};
        const tags = {};

        Object.keys(keys || {}).forEach((key) => {
            // by separating key 'definitions' (what's passed in) and key 'details' (stored in the StateContainer)
            // we can use different semantics for each
            // (atm we just change some functions to more useful internal names)
            keyDetails[key] = {
                initialValue: keys[key].initialValue,
                defaultValue: keys[key].defaultValue,
                isEqual: keys[key].eqCheck,
                isValid: keys[key].validator,
            };

            // instead of tags by key, we want to represent them as keys by tag
            (keys[key].tags || []).forEach((tag) => {
                tags[tag] = (tags[tag] || []).concat(key);
            });
        });

        if (parent) {
            // if we have a parent, let them manage our state keys/tags and subscriptions
            // if someone calls getState() or onChange() etc. on us later we'll just defer to them
            parent._addChild(this, tags, keyDetails);
            this._parent = parent;
        } else {
            this._stateContainer = new AppStateContainer(keyDetails, effectsCanMutateKeys);
            this._tags = tags;
            this._subscribers = {};
            this._nextSubscriberId = 0;
        }

        // all state providers (child or parent) handle their own effects
        this.effects = this._buildEffects(effects);

        log('initialised:', this);
    }

    getState(): AppStateProv.State {
        return this._getStateContainer().getState();
    }

    // returns an unsubscribe fn
    onChange(
        callback: AppStateProv.SubscriptionCallback,
        options: AppStateProv.SubscriptionOptions = {},
    ): () => void {
        return this._subscribe(callback, options);
    }

    keysByTag(tag: string): Array<string> {
        if (this._parent) {
            return this._parent.keysByTag(tag);
        }

        return this._tags[tag].slice();
    }

    private _addTags(tags) {
        Object.keys(tags).forEach((tag) => {
            this._tags[tag] = (this._tags[tag] || []).concat(tags[tag]);
        });
    }

    private _addChild(child, tags, keyDetails) {
        if (this._parent) {
            this._parent._addChild(child, tags, keyDetails);
        } else {
            this._stateContainer.addKeys(keyDetails);
            this._addTags(tags);
        }
    }

    private _getStateContainer() {
        // climb the tree till we get to the top!
        if (this._parent) {
            return this._parent._getStateContainer();
        }

        return this._stateContainer;
    }

    private _subscribe(
        onChange: AppStateProv.SubscriptionCallback,
        options: AppStateProv.SubscriptionOptions,
    ): () => void {
        if (this._parent) {
            return this._parent._subscribe(onChange, options);
        }

        const id = this._nextSubscriberId++;

        this._subscribers[id] = {
            onChange,
            watchedKeys: options.keys,
            watchedTags: options.tags,
        };

        if (options.runNow) {
            const currentState = this.getState();
            onChange(currentState, currentState, new Set());
        }

        return () => {
            delete this._subscribers[id];
        };
    }

    private _pushUpdate(
        newState: AppStateProv.State,
        oldState: AppStateProv.State,
        changedKeys: Set<string>,
        silenceListenersToTag: string,
    ): Promise<any> {
        if (this._parent) {
            return this._parent._pushUpdate(newState, oldState, changedKeys, silenceListenersToTag);
        }

        const keyHasChanged = (key) => changedKeys.has(key);

        const tagHasChanged = (tag) => {
            return (
                silenceListenersToTag !== tag &&
                Object.keys(this._tags)
                    .filter((tag) => [...changedKeys].some((key) => this._tags[tag].includes(key)))
                    .includes(tag)
            );
        };

        return changedKeys.size === 0
            ? Promise.resolve()
            : Promise.all(
                  Object.keys(this._subscribers).map((id) => {
                      const sub = this._subscribers[id];
                      if (
                          (!sub.watchedKeys && !sub.watchedTags) ||
                          (sub.watchedKeys && sub.watchedKeys.some(keyHasChanged)) ||
                          (sub.watchedTags && sub.watchedTags.some(tagHasChanged))
                      ) {
                          return sub.onChange(newState, oldState, changedKeys);
                      }
                  }),
              );
    }

    private _buildEffect(effectDefinitions: AppStateProv.EffectDefinitions, effectKey: string) {
        return async (...effectArgs) => {
            // when an effect is run, it uses its args to build a StateUpdateFn
            const updateFnOrConfig: AppStateProv.StateUpdateFn | AppStateProv.StateUpdateConfig =
                await effectDefinitions[effectKey](...effectArgs);

            const updateFn: AppStateProv.StateUpdateFn =
                typeof updateFnOrConfig === 'function'
                    ? updateFnOrConfig
                    : updateFnOrConfig.updateFn;

            if (typeof updateFn !== 'function') {
                throw new Error(
                    `Effect definition for '${effectKey}' didn't provide an update function`,
                );
            }

            const silenceListenersToTag: string =
                typeof updateFnOrConfig === 'function'
                    ? undefined
                    : updateFnOrConfig.silenceListenersToTag;

            try {
                const stateContainer = this._getStateContainer();
                // then it sends the StateUpdateFn off to the StateContainer,
                // which applies the function to the current state
                // and gives back a new state, as well as a set of keys that have changed
                const { changedKeys, oldState, newState } = stateContainer.updateState(updateFn);

                log('ran effect:', effectKey, 'args:', [...effectArgs], '=>', newState);

                // finally we let any subscribers to the keys that have changed know that there's been a state change
                // waiting for any async subscribers to resolve before we resolve ourselves
                // it's worth noting that the update will always happen on the next microtask.
                // meaning that another effect could take place between this state change and
                // the subscribers being called; that's okay though as they will always happen in order.
                // it just means that inside the onChange callback the state received may not be the exact current state.
                // but that means there will be another callback queued ready to go.
                await this._pushUpdate(newState, oldState, changedKeys, silenceListenersToTag);

                return this;
            } catch (e) {
                logError(`Could not run effect '${effectKey}'`);
                throw e;
            }
        };
    }

    private _buildEffects(definitions: AppStateProv.EffectDefinitions) {
        return Object.keys(definitions || {}).reduce(
            (effects, key) =>
                Object.defineProperty(effects, key, {
                    value: this._buildEffect(definitions, key),
                }),
            Object.create(null),
        );
    }
}
